import clsx from 'clsx';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import QMarkL from '../_assets/q-l.svg?react';
import QMarkRBig from '../_assets/q-r-big.svg?react';
import QMarkR from '../_assets/q-r.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  allWinners: CompetitionWinner[];
  displayWarningWhenKIn: string[] | undefined;
}) => {
  return (
    <>
      <div
        className="relative isolate flex w-full flex-col overflow-hidden text-white"
        style={{
          background: 'linear-gradient(180deg, #18A0FB 0%, #007BCD 100%)',
        }}
      >
        <div className="mx-auto w-full max-w-[870px] p-6 lg:px-0">
          <Content
            activeCompetition={props.activeCompetition}
            winners={props.winners}
            allWinners={props.allWinners}
            displayWarningWhenKIn={props.displayWarningWhenKIn}
          />
        </div>

        <Background />
      </div>

      <Disclaimer />
    </>
  );
};

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <div className="container mx-auto px-5 text-center typography-v1-body4 text-balance">
      <LoanExampleText />
    </div>
  </div>
);

const Card = (props: React.ComponentProps<'div'>) => (
  <div
    {...props}
    className={clsx('rounded-2xl bg-[#FBFDFF] p-4 text-[#223C66] sm:p-8', props.className)}
    style={{
      boxShadow: '2px 4px 40px 10px rgba(199, 205, 209, 0.20)',
      ...props.style,
    }}
  />
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
  allWinners: CompetitionWinner[];
  displayWarningWhenKIn: string[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);
  const [isAllOpen, setIsAllOpen] = useState(false);

  return (
    <div className="flex flex-col pt-4 pb-52 sm:pb-80 md:pt-8 md:pb-90 xl:pb-20">
      <h1 className="mb-4 text-center text-4xl leading-[1.2] font-bold sm:mb-12 sm:text-5xl xl:text-[4.3rem]">
        Gjett om du kan få <br />
        <UnderlinedText variant="2" distance="0.2em">
          lavere rente
        </UnderlinedText>{' '}
        med Scoopr
      </h1>

      <Card className="mb-4 text-center typography-v1-body1">
        Én vinner får nedbetalt inntil{' '}
        <span className="font-bold text-[#18A0FB]">500&nbsp;000&nbsp;kr</span> av sin gjeld,
        boliglån inkludert. Gjett riktig på om du kan få lavere rente på din forbruks- og
        kredittkortgjeld for å delta.
      </Card>

      <div className="flex flex-col gap-4 md:flex-row">
        <Card className="flex-1">
          <h3 className="mb-2 typography-v1-h3 md:mb-4">For å delta:</h3>
          <div className="mb-4 flex flex-col">
            <NumberedList
              renderNumber={(props) => (
                <NumberCircle
                  sx={{
                    fontSize: 20,
                    height: 32,
                    backgroundColor: '#FDD183',
                    color: '#354661',
                    fontWeight: 700,
                  }}
                  {...props}
                />
              )}
              renderLabel={(props) => <div {...props} className="ml-1 typography-v1-body3" />}
            >
              <NumberedListItem>Logg inn med bankID</NumberedListItem>
              <NumberedListItem>Gjett om du kan få lavere rente</NumberedListItem>
              <NumberedListItem>Se fasit og delta i trekningen</NumberedListItem>
            </NumberedList>
          </div>

          <a
            href={routes.APRIL_2026_COMPETITION_TERMS}
            className="mb-6 block typography-v1-body4 underline"
          >
            Vilkår og betingelser for deltagelse
          </a>

          {ended && (
            <p className="-mt-4 mb-4 typography-v1-h4">
              <span>Denne trekningen er avsluttet. </span>
              <button
                onClick={() => setIsOpen(true)}
                className="mb-0.5 ml-1 font-semibold text-[#18A0FB] underline"
              >
                Se vinneren her
              </button>
            </p>
          )}

          <CTA
            activeCompetition={props.activeCompetition}
            displayWarningWhenKIn={props.displayWarningWhenKIn}
            onClick={() => {
              sendUserPortalEvent('apr26_competition_cta_top_clicked');
            }}
          />
        </Card>

        <Card className="flex flex-1 flex-col gap-4">
          <h3 className="typography-v1-body3">Tidligere vinnere:</h3>

          {props.allWinners.slice(0, 3).map((winner, index) => (
            <div
              key={winner.name + winner.age + winner.city}
              className="flex items-center gap-2 rounded-lg bg-[#F0F4FA] px-4 py-3 typography-v1-body4 sm:py-4"
            >
              <span className="flex size-6 items-center justify-center rounded-full bg-[#031839] text-[10px] leading-none font-bold text-white">
                {props.allWinners.length - index}
              </span>
              <span className="flex-2">{winner.name}</span>
              <span className="text-nowrap">{winner.age}</span>
              <span className="flex-1 text-right">{winner.city}</span>
            </div>
          ))}

          <button
            onClick={() => setIsAllOpen(true)}
            className="mr-auto typography-v1-body4 text-[#18A0FB]"
          >
            Se alle
          </button>
        </Card>
      </div>

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}

      <CompetitionWinnersDialog
        open={isAllOpen}
        onClose={() => setIsAllOpen(false)}
        winners={props.allWinners}
      />
    </div>
  );
};

const Background = () => (
  <div className="-z-10">
    <QMarkRBig className="absolute -right-8 bottom-0 -mb-10 origin-bottom scale-75 sm:right-1/12 sm:scale-100 xl:right-1/2 xl:bottom-40 xl:translate-x-180 2xl:translate-x-220" />
    <QMarkR className="absolute top-0 right-0 hidden xl:top-30 xl:right-1/2 xl:block xl:translate-x-140" />
    <QMarkL className="absolute bottom-0 left-0 hidden xl:bottom-80 xl:left-1/2 xl:block xl:-translate-x-180 2xl:-translate-x-220" />
  </div>
);
